export interface FuzzyMatch {
  score: number;
  /** Indexes of the matched characters in the text, for highlighting. */
  positions: number[];
}

const isWordStart = (text: string, i: number) =>
  i === 0 || /[\s/._\-:]/.test(text[i - 1] ?? "") || /[a-z][A-Z]/.test(text.slice(i - 1, i + 1));

/**
 * Matches `query` against `text` as a case-insensitive subsequence, like VS Code's quick open.
 * Undefined when it doesn't match. Higher scores are better: consecutive letters and letters at
 * word starts count extra, and gaps cost a little.
 */
export function fuzzyMatch(query: string, text: string): FuzzyMatch | undefined {
  const q = query.replace(/\s+/g, "").toLowerCase();
  if (!q) return { score: 0, positions: [] };
  const lower = text.toLowerCase();
  const positions: number[] = [];
  let score = 0;
  let from = 0;
  for (const char of q) {
    // Prefer the next word start that has this letter, else its next occurrence.
    let index = lower.indexOf(char, from);
    if (index < 0) return undefined;
    for (let i = index; i < lower.length; i++) {
      if (lower[i] === char && isWordStart(text, i)) {
        if (positions.length === 0 || i === index) index = i;
        break;
      }
    }
    const previous = positions.at(-1);
    score += 1;
    if (previous !== undefined && index === previous + 1) score += 3;
    if (isWordStart(text, index)) score += 2;
    if (previous !== undefined) score -= Math.min(index - previous - 1, 3) * 0.1;
    positions.push(index);
    from = index + 1;
  }
  return { score: score - text.length * 0.01, positions };
}

/** Items that match `query`, best first (ties keep their original order). */
export function fuzzyFilter<T>(
  items: readonly T[],
  query: string,
  text: (item: T) => string,
): { item: T; match: FuzzyMatch }[] {
  return items
    .flatMap((item, order) => {
      const match = fuzzyMatch(query, text(item));
      return match ? [{ item, match, order }] : [];
    })
    .sort((a, b) => b.match.score - a.match.score || a.order - b.order)
    .map(({ item, match }) => ({ item, match }));
}
